// FILE: src/pages/Home/Home.jsx
import React from "react";
import DATA from "../../data";
import Section from "../../components/Section/Section";
import styles from "./Home.module.css";

const yearsInBusiness = () => Math.max(1, new Date().getFullYear() - DATA.company.sinceYear);
const mailto = `mailto:${DATA.company.email}?subject=${encodeURIComponent(
  "Project inquiry"
)}&body=${encodeURIComponent("Tell us about your project and timeline:")}`;

// Font Awesome 5.15.4 icon map
const faIcon = (id) => {
  switch (id) {
    case "compass": return "fas fa-compass";
    case "window": return "fas fa-window-maximize";
    case "phone": return "fas fa-mobile-alt";
    case "db": return "fas fa-database";
    case "cloud": return "fas fa-cloud";
    case "team": return "fas fa-users";
    default: return "fas fa-star";
  }
};

export default function Home() {
  // add near top of the component:
  React.useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);


  const years = yearsInBusiness();
  return (
    <>
      {/* HERO */}
      {/* HERO */}
      <Section id="home" className="hero-gradient">
        <div className={`${styles.heroWrap} ${styles.heroPad} container`}>
          <div className={styles.heroMesh} aria-hidden="true" />
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <span className="badge text-bg-soft mb-3">
                {years}+ years • {DATA.company.location}
              </span>

              <h1 className={`display-5 fw-bold text-ink mb-3 ${styles.headline}`}>
                Web &amp; mobile products, engineered to last.
              </h1>

              <p className={`text-muted-2 mb-4 ${styles.clamp2} ${styles.heroBlurb}`}>
                Senior-led studio shipping dependable software with clean architecture and thoughtful design.
                Start fast. Scale safely.
              </p>

              <div className="d-flex gap-2 flex-wrap">
                <a href={mailto} className="btn btn-brand btn-lg">Start a project</a>
                {DATA.company.ctaCalendar && (
                  <a href={DATA.company.ctaCalendar} className="btn btn-outline-secondary btn-lg">
                    Book a call
                  </a>
                )}
                <a href="#work" data-anchor="1" className="btn btn-link btn-lg p-0 ms-lg-2">
                  See our work →
                </a>
              </div>

              <ul className={`${styles.metaList} mt-3`}>
                <li><i className="fas fa-check" aria-hidden="true"></i> Senior team</li>
                <li><i className="fas fa-check" aria-hidden="true"></i> Outcome-driven</li>
                <li><i className="fas fa-check" aria-hidden="true"></i> Fast, steady delivery</li>
              </ul>
            </div>

            <div className="col-lg-5">
              <div className={`p-4 card-surface rounded-4 ${styles.cardFancy} ${styles.heroCard}`}>
                <h6 className="text-muted mb-3">Capabilities</h6>
                <div className="row g-3">
                  {DATA.services.slice(0, 4).map((s) => (
                    <div className="col-6" key={s.title}>
                      <div className={styles.serviceItem}>
                        <span className={`${styles.iconPill} me-2`}>
                          <i className={`${faIcon(s.icon)}`} aria-hidden="true"></i>
                        </span>
                        <div>
                          <div className="fw-semibold">{s.title}</div>
                          <div className={`small text-muted-2 ${styles.clamp2}`}>{s.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <a href="#services" className="btn btn-link mt-3 p-0" data-anchor="1">See all services →</a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* SERVICES */}
      <Section id="services" className="bg-faint">
        <div className="container">
          <div className="mb-3">
            <h2 className={`fw-bold text-ink ${styles.sectionTitle}`}>Services</h2>
          </div>
          <div className="row g-4">
            {DATA.services.map((s) => (
              <div className="col-md-6 col-lg-4" key={s.title}>
                <div className={`card h-100 border-0 ${styles.cardFancy}`}>
                  <div className="card-body">
                    <div className="d-flex align-items-start mb-2">
                      <span className={`${styles.iconPill} me-2`}>
                        <i className={`${faIcon(s.icon)}`} aria-hidden="true"></i>
                      </span>
                      <h5 className="card-title mb-0">{s.title}</h5>
                    </div>
                    <p className={`text-muted-2 mb-0 ${styles.clamp2}`}>{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* WORK */}
      <Section id="work">
        <div className="container">
          <div className="mb-3">
            <h2 className={`fw-bold text-ink ${styles.sectionTitle}`}>Selected Work</h2>
          </div>
          <div className="row g-4">
            {DATA.work.map((w) => (
              <div className="col-md-6 col-lg-4" key={w.title}>
                <div className={`card h-100 border-0 ${styles.cardFancy}`}>
                  <div className="card-body d-flex flex-column">
                    <div className={styles.thumb}>
                      <span className={styles.thumbBadge}>
                        <i className="fas fa-external-link-alt me-1" aria-hidden="true"></i>Live
                      </span>
                    </div>
                    <div className="d-flex align-items-center mt-3 mb-2">
                      <i className={`fas fa-star me-2 ${styles.fa32}`} aria-hidden="true"></i>
                      <h5 className="mb-0">{w.title}</h5>
                    </div>
                    <p className={`text-muted-2 flex-grow-1 mb-2 ${styles.clamp2}`}>{w.desc}</p>
                    <div className="d-flex flex-wrap gap-2">
                      {w.tags.map((t) => (
                        <span key={t} className="badge badge-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.cardFooterLine} />
                  <div className="card-footer bg-surface border-0">
                    <a href={w.link} className="btn btn-sm btn-outline-secondary" target="_blank" rel="noreferrer">
                      Visit
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* APPROACH */}
      <Section id="approach" className="bg-faint">
        <div className="container">
          <div className="mb-3">
            <h2 className={`fw-bold text-ink ${styles.sectionTitle}`}>Our Approach</h2>
          </div>
          <div className="row g-4">
            {DATA.approach.map((a, i) => (
              <div className="col-md-6 col-lg-4" key={a.title}>
                <div className={`h-100 p-4 card-surface rounded-4 ${styles.cardFancy}`}>
                  <div className="d-flex align-items-center mb-2">
                    <span className={styles.stepBadge}>{i + 1}</span>
                    <h5 className="mb-0 ms-2">{a.title}</h5>
                  </div>
                  <p className={`text-muted-2 mb-0 ${styles.clamp2}`}>{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* TECH */}
      <Section id="tech">
        <div className="container">
          <div className="mb-3">
            <h2 className={`fw-bold text-ink ${styles.sectionTitle}`}>Tech We Use</h2>
          </div>
          <div className="row g-3">
            {DATA.tech.map((t) => (
              <div className="col-6 col-md-4 col-lg-3" key={t.name}>
                <div className={`p-3 rounded-4 card-surface d-flex justify-content-between align-items-center ${styles.techChip}`}>
                  <span className="fw-semibold">{t.name}</span>
                  <span className="small text-muted-2">{t.kind}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ABOUT & CLIENTS */}
      <Section id="about" className="bg-faint">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-7">
              <h2 className={`fw-bold text-ink ${styles.sectionTitle}`}>About TekiMart</h2>
              <p className={`text-muted-2 ${styles.clamp3}`}>
                Your satisfaction is our norm. We believe your success is our success. Founded in 2015 in Sri Lanka, now in Canada serving globally—ready to start when you are.
              </p>
              <ul className={`list-unstyled ${styles.timeline}`}>
                {DATA.timeline.map((t, i) => (
                  <li className="d-flex align-items-start gap-3 mb-3" key={i}>
                    <div className="timeline-dot mt-1"></div>
                    <div>
                      <div className="fw-semibold">{t.year}</div>
                      <div className={`text-muted-2 ${styles.clamp2}`}>{t.text}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-lg-5">
              <h2 className={`fw-bold text-ink ${styles.sectionTitle}`}>Clients</h2>
              <ul className={`list-group list-group-flush ${styles.clientsList}`}>
                {DATA.clients.map((c) => (
                  <li key={c} className={`list-group-item card-surface d-flex align-items-center ${styles.clientItem}`}>
                    <i className="fas fa-check me-2" aria-hidden="true"></i> {c}
                  </li>
                ))}
              </ul>
              {/* Keep testimonials minimal — just one concise quote */}
              <div className="divider-x my-4" />
              <div className={`p-4 card-surface rounded-4 ${styles.cardFancy}`}>
                <p className="mb-2">“Clear decisions, clean architecture, on-time delivery.”</p>
                <footer className="small text-muted-2">— Director of Engineering, Growth SaaS</footer>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CONTACT (trimmed copy) */}
      <Section id="contact">
        <div className="container">
          <div className={`p-4 p-lg-5 card-surface rounded-4 ${styles.cta}`}>
            <div className="row g-3 align-items-center">
              <div className="col-lg-7">
                <h2 className={`fw-bold text-ink mb-0 ${styles.sectionTitle}`}>Let’s build something great.</h2>
              </div>
              <div className="col-lg-5 text-lg-end">
                <a href={mailto} className="btn btn-brand btn-lg me-2">Email us</a>
                {DATA.company.ctaCalendar && (
                  <a href={DATA.company.ctaCalendar} className="btn btn-outline-secondary btn-lg">Book a call</a>
                )}
              </div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
